import { useCallback } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { FormikHelpers } from 'formik'
import { createContainer } from 'unstated-next'
import * as api from 'lib/api/pointOfSale'
import CatalogItem from './catalogItem'
import { AxiosError } from 'axios'
import { Discount } from 'types/model'

export interface DiscountFormValues {
  discountCode: string
}

type ApplyDiscount = (
  values: DiscountFormValues,
  actions: FormikHelpers<DiscountFormValues>
) => Promise<void>

export interface DiscountContainer {
  discount?: Discount
  applyDiscount: ApplyDiscount
  clearDiscount: () => void
}

const useDiscount = () => {
  const { catalogItem } = CatalogItem.useContainer()
  const [discount, setDiscount] = useLocalStorage<Discount | undefined>(
    'discount',
    undefined
  )

  const applyDiscount = useCallback<ApplyDiscount>(
    async (values, actions) => {
      const { discountCode } = values
      const { setFieldError, setSubmitting, setErrors } = actions

      try {
        const res = await api.applyDiscount({
          discountCode,
          items: [{ sku: catalogItem.sku, quantity: 1 }],
        })

        if (res.status !== 200) {
          setFieldError('discountCode', res.statusText)
          setSubmitting(false)
          return
        }

        const response = await res.data

        if (response.isValid) {
          setDiscount({ code: discountCode, amount: response.amount })
        } else {
          setFieldError('discountCode', 'Invalid code')
          setSubmitting(false)
        }
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          setErrors(err.response?.data.errors)
        } else if (err instanceof Error) {
          setFieldError('discountCode', err.message)
        }

        setSubmitting(false)
      }
    },
    [catalogItem, setDiscount]
  )

  const clearDiscount = useCallback(() => {
    setDiscount(undefined)
  }, [setDiscount])

  return { discount, applyDiscount, clearDiscount }
}

export default createContainer<DiscountContainer>(useDiscount)
